import React, { createContext, useContext, useState, useCallback } from 'react';
import { avizAPI } from '../services/api/avizAPI';

/**
 * Aviz Context for state management
 * Handles all aviz-related state and operations
 */
const AvizContext = createContext();

export const useAviz = () => {
  const context = useContext(AvizContext);
  if (!context) {
    throw new Error('useAviz must be used within an AvizProvider');
  }
  return context;
};

export const AvizProvider = ({ children }) => {
  // State management
  const [avize, setAvize] = useState({}); // { transportId: [aviz1, aviz2, ...] }
  const [currentAviz, setCurrentAviz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAvizModal, setShowAvizModal] = useState(false);
  const [pendingTransportData, setPendingTransportData] = useState(null);

  // Fetch all avize for a transport
  const fetchAvize = useCallback(async (transportId, authToken) => {
    setLoading(true);
    setError(null);
    try {
      const data = await avizAPI.getAll(transportId, authToken);
      setAvize(prev => ({
        ...prev,
        [transportId]: data
      }));
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch avize:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new aviz
  const createAviz = useCallback(async (transportId, avizData, authToken) => {
    setLoading(true);
    setError(null);
    try {
      const newAviz = await avizAPI.create(transportId, avizData, authToken);

      // Update local state
      setAvize(prev => ({
        ...prev,
        [transportId]: [...(prev[transportId] || []), newAviz]
      }));

      return { success: true, data: newAviz };
    } catch (err) {
      setError(err.message);
      console.error('Failed to create aviz:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Create aviz from transport data (for transport creation flow)
  const createAvizFromTransport = useCallback(async (transportId, transportData, authToken) => {
    setLoading(true);
    setError(null);
    try {
      const newAviz = await avizAPI.createFromTransport(transportId, transportData, authToken);

      // Update local state
      setAvize(prev => ({
        ...prev,
        [transportId]: [...(prev[transportId] || []), newAviz]
      }));

      // Set as current aviz for editing
      setCurrentAviz(newAviz);

      return { success: true, data: newAviz };
    } catch (err) {
      setError(err.message);
      console.error('Failed to create aviz from transport:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update existing aviz
  const updateAviz = useCallback(async (transportId, avizId, updateData, authToken) => {
    setLoading(true);
    setError(null);
    try {
      const updatedAviz = await avizAPI.update(transportId, avizId, updateData, authToken);

      // Update local state
      setAvize(prev => ({
        ...prev,
        [transportId]: prev[transportId]?.map(aviz =>
          aviz.id === avizId ? updatedAviz : aviz
        ) || []
      }));

      // Update current aviz if it's the one being edited
      if (currentAviz?.id === avizId) {
        setCurrentAviz(updatedAviz);
      }

      return { success: true, data: updatedAviz };
    } catch (err) {
      setError(err.message);
      console.error('Failed to update aviz:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [currentAviz]);

  // Delete aviz
  const deleteAviz = useCallback(async (transportId, avizId, authToken) => {
    setLoading(true);
    setError(null);
    try {
      await avizAPI.delete(transportId, avizId, authToken);

      // Update local state
      setAvize(prev => ({
        ...prev,
        [transportId]: prev[transportId]?.filter(aviz => aviz.id !== avizId) || []
      }));

      // Clear current aviz if it's the one being deleted
      if (currentAviz?.id === avizId) {
        setCurrentAviz(null);
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete aviz:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [currentAviz]);

  // Upload document for aviz
  const uploadAvizDocument = useCallback(async (transportId, avizId, file, authToken) => {
    setLoading(true);
    setError(null);
    try {
      const updatedAviz = await avizAPI.uploadDocument(transportId, avizId, file, authToken);

      // Update local state
      setAvize(prev => ({
        ...prev,
        [transportId]: prev[transportId]?.map(aviz =>
          aviz.id === avizId ? updatedAviz : aviz
        ) || []
      }));

      return { success: true, data: updatedAviz };
    } catch (err) {
      setError(err.message);
      console.error('Failed to upload document:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Open modal for aviz completion
  const openAvizModal = useCallback((transportData = null) => {
    setPendingTransportData(transportData);
    setShowAvizModal(true);
  }, []);

  // Close aviz modal
  const closeAvizModal = useCallback(() => {
    setShowAvizModal(false);
    setPendingTransportData(null);
    setCurrentAviz(null);
  }, []);

  // Calculate completion percentage for an aviz
  const calculateCompletionPercentage = useCallback((aviz) => {
    if (!aviz) return 0;

    const fields = [
      'seria', 'numar', 'data_emitere',
      'furnizor_denumire', 'furnizor_adresa', 'furnizor_cif',
      'cumparator_denumire', 'cumparator_adresa', 'cumparator_cif',
      'bunuri', 'valoare_totala', 'moneda',
      'expeditor_semnatura', 'expeditor_stampila',
      'transportator_nume', 'transportator_ci_serie', 'transportator_ci_numar',
      'numar_mijloc_transport', 'ora_estimativa_livrare', 'transportator_semnatura',
      'document'
    ];

    let completedFields = 0;
    fields.forEach(field => {
      const value = aviz[field];
      if (value !== null && value !== undefined && value !== '' &&
          (Array.isArray(value) ? value.length > 0 : true)) {
        completedFields++;
      }
    });

    return Math.round((completedFields / fields.length) * 100);
  }, []);

  const value = {
    // State
    avize,
    currentAviz,
    loading,
    error,
    showAvizModal,
    pendingTransportData,

    // Actions
    fetchAvize,
    createAviz,
    createAvizFromTransport,
    updateAviz,
    deleteAviz,
    uploadAvizDocument,
    openAvizModal,
    closeAvizModal,
    setCurrentAviz,
    calculateCompletionPercentage,
  };

  return <AvizContext.Provider value={value}>{children}</AvizContext.Provider>;
};